// src/components/upload/FileUpload.jsx
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Alert from '../Alert';
import { formatFileSize } from '@utils/helpers';
import { FILE_CONFIG } from '@config/constants';

const getExt = file =>
  (file?.name && '.' + file.name.split('.').pop().toLowerCase()) || '';

export function FileUpload({ onFileUpload, isLoading, error: uploadError }) {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState(null);
  const [fileName, setFileName] = useState('');

  const acceptAttr = useMemo(
    () => (FILE_CONFIG.supportedFormats || ['.pdf', '.docx', '.doc', '.txt']).join(','),
    []
  );

  const validate = (file) => {
    if (!file) return 'No file provided.';
    if (file.size > FILE_CONFIG.maxSize) {
      return `File too large. Max is ${formatFileSize(FILE_CONFIG.maxSize)}.`;
    }
    const ext = getExt(file);
    const allowed = Array.isArray(FILE_CONFIG.supportedFormats)
      ? FILE_CONFIG.supportedFormats
      : ['.pdf', '.docx', '.doc', '.txt'];
    if (!allowed.includes(ext)) {
      return `Unsupported file type (${ext || 'unknown'}). Allowed: ${allowed.join(', ')}`;
    }
    return null;
  };

  const handleFiles = useCallback(
    async (files) => {
      setLocalError(null);
      const file = files?.[0];
      const problem = validate(file);
      if (problem) {
        setLocalError(problem);
        return;
      }
      setFileName(file.name);
      try {
        await onFileUpload?.(file);
      } catch (err) {
        setLocalError(err?.message || 'Upload failed.');
      }
    },
    [onFileUpload]
  );

  const onChange = (e) => handleFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <div
      className="rounded-xl border-2 border-dashed border-cyan-200 bg-cyan-50/30 p-6 text-center"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={onChange}
      />

      {/* Icon */}
      <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-cyan-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-600" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a5 5 0 00-4.546 2.916A4.5 4.5 0 007.5 14H9v-2H7.5a2.5 2.5 0 01-.243-4.993l.243-.007h.413a1 1 0 00.928-.629A3 3 0 1115 8h1a4 4 0 010 8h-1v2h1a6 6 0 10-4-10.392V5a1 1 0 10-2 0v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H9v2h3a1 1 0 110 2H8a1 1 0 01-1-1v-2H6.5A4.5 4.5 0 017 5.084 7 7 0 1119 12a7 7 0 01-7 7h-1v-2h1a5 5 0 000-10h-1V5a3 3 0 116 0h-2a1 1 0 110-2h2A5 5 0 0012 2z" />
        </svg>
      </div>

      <p className="font-medium text-gray-800">Drag & drop your resume here</p>
      <p className="text-sm text-gray-600">
        or{' '}
        <button
          type="button"
          className="text-cyan-600 underline hover:text-cyan-700"
          onClick={() => inputRef.current?.click()}
          disabled={isLoading}
        >
          click to browse
        </button>
      </p>

      {fileName && (
        <p className="mt-2 text-sm text-gray-700">
          Selected: <strong>{fileName}</strong>
        </p>
      )}

      {(localError || uploadError) && (
        <div className="mt-3">
          <Alert type="error" message={localError || uploadError} />
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500">
        Supports {acceptAttr} (Max {formatFileSize(FILE_CONFIG.maxSize)})
      </p>
    </div>
  );
}

export default FileUpload;
